/**
 * Shared nudge state — V98-3 review U14. One record per item key for the
 * whole workspace session (kept in the react-query cache), so a nudge sent
 * from "Open by owner" shows as sent in Legal's in-force gaps, the item
 * page's status block, and anywhere else the same item appears — and the
 * button never offers a second nudge the server would refuse (one per item
 * per day). The server stays the authority: POST /nudges (lead / admin).
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { nudge } from './coverage-api';

export type NudgeState =
  | { kind: 'sent'; to: string }
  | { kind: 'already' }
  | { kind: 'away'; until: string }
  | { kind: 'error'; text: string };

const key = (slug: string) => ['nudge-state', slug] as const;

/** Message for the live region after a nudge attempt. */
export function nudgeMessage(s: NudgeState, who: string): string {
  return s.kind === 'sent' ? `Nudge sent to ${s.to}` : s.kind === 'already' ? 'Already nudged today' : s.kind === 'away' ? `${who} is away` : 'The nudge could not be sent';
}

export function useNudges(slug: string) {
  const qc = useQueryClient();
  const q = useQuery<Record<string, NudgeState>>({ queryKey: key(slug), queryFn: () => ({}), initialData: {}, staleTime: Infinity, gcTime: Infinity });
  const send = useCallback(async (itemKey: string, holder: { user_id: string | null; name: string }): Promise<NudgeState> => {
    let next: NudgeState;
    try {
      const r = await nudge(slug, { item_key: itemKey, holder_user_id: holder.user_id ?? undefined });
      next = { kind: 'sent', to: r.nudge.for_user_id ? `${holder.name}’s delegate` : holder.name };
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { code?: string; absent_until?: string } | undefined) : undefined;
      next = e instanceof ApiError && e.status === 429 ? { kind: 'already' }
        : body?.code === 'holder_absent' ? { kind: 'away', until: body.absent_until ?? '' }
        : { kind: 'error', text: e instanceof Error && e.message ? e.message : 'The nudge could not be sent.' };
    }
    qc.setQueryData<Record<string, NudgeState>>(key(slug), (s) => ({ ...(s ?? {}), [itemKey]: next }));
    return next;
  }, [qc, slug]);
  return { state: q.data ?? {}, send };
}
